import React, {ChangeEvent} from 'react';
import {observer} from 'mobx-react';
import I18n from 'app/i18n';
import {faqStore} from 'app/store/global';
import type {FaqArticleLink} from 'app/utils/knowledgeCenter/faqGrouping';
import SmartFullPageLoader from 'app/components/reusable/smart_loaders/SmartFullPageLoader';
import SmartPageLoader from 'app/components/reusable/smart_loaders/SmartPageLoader';
import SearchInput from 'app/components/SearchInput';
import KnowledgeCenterCategoryGrid from 'app/components/pages/faq/knowledgeCenter/KnowledgeCenterCategoryGrid';
import KnowledgeCenterPopularArticles from 'app/components/pages/faq/knowledgeCenter/KnowledgeCenterPopularArticles';
import KnowledgeCenterSupportBlock from 'app/components/pages/faq/knowledgeCenter/KnowledgeCenterSupportBlock';
import KnowledgeCenterArticlePanel from 'app/components/pages/faq/knowledgeCenter/KnowledgeCenterArticlePanel';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';

interface State {
    search: string
    summaryFetchStarted: boolean
}

class FaqIndex extends React.Component<object, State> {
    state: State = {
        search: '',
        summaryFetchStarted: faqStore.summaryFaqs.length > 0
    };

    componentDidMount(): void {
        if (faqStore.summaryFaqs.length === 0) {
            this.setState({summaryFetchStarted: true});
        }

        faqStore.loadMeta();
        faqStore.loadSummary();
    }

    handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const search = event.currentTarget.value;

        this.setState({search});

        if (search.trim()) {
            faqStore.loadData();
        }
    };

    handleClearSearch = (): void => {
        this.setState({search: ''});
    };

    getFilteredArticles(trimmedSearch: string): FaqArticleLink[] {
        if (!trimmedSearch) {
            return [];
        }

        return faqStore.searchFaqs(trimmedSearch).map(({name, title}) => ({name, title}));
    }

    isInitialHubLoading(): boolean {
        const hasSummaryData = faqStore.summaryFaqs.length > 0;

        return !hasSummaryData && (!this.state.summaryFetchStarted || faqStore.summaryLoading);
    }

    isSearchResultsLoading(trimmedSearch: string): boolean {
        return Boolean(trimmedSearch) && faqStore.loading && faqStore.faqs.length === 0;
    }

    render(): React.ReactElement {
        const {search} = this.state;
        const trimmedSearch = search.trim();
        const isSearchMode = Boolean(trimmedSearch);

        if (this.isInitialHubLoading()) {
            return <SmartFullPageLoader />;
        }

        const filteredArticles = this.getFilteredArticles(trimmedSearch);
        const isSearchResultsLoading = this.isSearchResultsLoading(trimmedSearch);

        return (
            <div className={`knowledge-center${isSearchMode ? ' knowledge-center--article' : ''}`}>
                <div className="content-header knowledge-center__header">
                    <h1 className="title-h1">{I18n.t('knowledge_center.title')}</h1>
                    <SearchInput
                        classSearchInput="knowledge-center__search"
                        value={search}
                        handleChange={this.handleSearchChange}
                        onClick={this.handleClearSearch}
                        placeholder={I18n.t('knowledge_center.search_placeholder')}
                    />
                </div>
                <div className="knowledge-center__body">
                    {isSearchMode
                        ? <section className="knowledge-center__category-main">
                            {isSearchResultsLoading
                                ? <SmartPageLoader />
                                : <KnowledgeCenterArticlePanel
                                    title={I18n.t('knowledge_center.search_results')}
                                    icon={(
                                        <SpriteIcon
                                            name="#svg-search-icon"
                                            className="svg-default knowledge-center__category-icon-img"
                                        />
                                    )}
                                    articles={filteredArticles}
                                />}
                        </section>
                        : <>
                            <KnowledgeCenterCategoryGrid categories={faqStore.faqCategories} />
                            <KnowledgeCenterPopularArticles articles={faqStore.popularFaqs} />
                            <KnowledgeCenterSupportBlock />
                        </>}
                </div>
            </div>
        );
    }
}

export default observer(FaqIndex);
